/**
 * Upcoming meetings (TRUS-691): a section in the Inbox sidebar (the caller's
 * meetings across conversations) and a list in a conversation's "Calls &
 * notes" panel (that conversation's). A row opens the meeting (its
 * conversation + the card); Join is right on the row once it opens.
 */

import React, { useState } from 'react';
import { CalendarClock, ChevronDown, ChevronRight, Loader2, Phone, Video as VideoIcon } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { meetingPhase, type Meeting } from '@/services/meetingsApi';
import { Button } from '../../ui/button';
import { formatMeetingRange } from './meetingTime';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { ItemZoneNote } from '@/components/ui/ItemZoneNote';
import { meetingStore } from './meetingStore';
import { useCanScheduleMeetings, useJoinMeeting, useNow, useUpcomingMeetings } from './useMeetings';

function MeetingRow({ meeting, now }: { meeting: Meeting; now: number }) {
  const { t, language } = useAppTranslation();
  const { join, pendingId } = useJoinMeeting();
  const tz = useUserTimezone(meeting.org_timezone);
  const phase = meetingPhase(meeting, now);
  const canJoin = phase === 'joinable' || phase === 'live';
  const Icon = meeting.mode === 'audio' ? Phone : VideoIcon;
  return (
    <li className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted" data-testid={`upcoming-meeting-${meeting.id}`} data-phase={phase}>
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        onClick={() => meetingStore.open(meeting.id)}
        data-testid={`upcoming-meeting-open-${meeting.id}`}
      >
        <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', phase === 'live' ? 'text-emerald-600' : 'text-muted-foreground')} aria-hidden="true" />
        <span className="min-w-0">
          <span className="block truncate text-sm">{meeting.title || t('messaging.meetings.untitled', 'Meeting')}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {phase === 'live' ? t('messaging.meetings.phaseLive', 'Live now') : formatMeetingRange(meeting.starts_at, null, language, tz)}
            {phase !== 'live' && <ItemZoneNote value={meeting.starts_at} itemTimeZone={meeting.timezone} kind="meeting" className="ml-1" />}
          </span>
        </span>
      </button>
      {canJoin && (
        <Button
          type="button"
          size="sm"
          className="h-7 bg-emerald-600 px-2 text-xs text-white hover:bg-emerald-700"
          disabled={pendingId === meeting.id}
          onClick={() => join(meeting)}
          data-testid={`upcoming-meeting-join-${meeting.id}`}
        >
          {pendingId === meeting.id ? <Loader2 className="h-3 w-3 animate-spin" /> : t('messaging.meetings.join', 'Join')}
        </Button>
      )}
    </li>
  );
}

/** A plain list (no chrome): a conversation's upcoming meetings. */
export function UpcomingMeetingsList({ channelId, limit = 5 }: { channelId?: number | null; limit?: number }) {
  const { t } = useAppTranslation();
  const now = useNow();
  const q = useUpcomingMeetings({ channelId: channelId ?? undefined });
  const items = (q.data ?? []).slice(0, limit);
  if (q.isLoading) return null;
  if (!items.length) {
    return (
      <p className="px-2 text-xs text-muted-foreground" data-testid="upcoming-meetings-empty">
        {t('messaging.meetings.noneUpcoming', 'No upcoming meetings.')}
      </p>
    );
  }
  return (
    <ul className="flex flex-col" data-testid="upcoming-meetings-list">
      {items.map((m) => (
        <MeetingRow key={m.id} meeting={m} now={now} />
      ))}
    </ul>
  );
}

/** The "Upcoming meetings" section in the Inbox sidebar (staff, meetings on). Hidden when there are none. */
export function UpcomingMeetingsSection() {
  const { t } = useAppTranslation();
  const enabled = useCanScheduleMeetings();
  const now = useNow();
  const [open, setOpen] = useState(true);
  const q = useUpcomingMeetings({ mine: true, enabled });
  const items = q.data ?? [];
  if (!enabled || !items.length) return null;
  return (
    <section className="border-t px-2 py-2" aria-label={t('messaging.meetings.upcoming', 'Upcoming meetings')} data-testid="upcoming-meetings-section">
      <button
        type="button"
        className="flex w-full items-center gap-1 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <CalendarClock className="h-3 w-3" aria-hidden="true" />
        <span className="flex-1 text-left">{t('messaging.meetings.upcoming', 'Upcoming meetings')}</span>
        <span>{items.length}</span>
      </button>
      {open && (
        <ul className="mt-1 flex flex-col">
          {items.slice(0, 8).map((m) => (
            <MeetingRow key={m.id} meeting={m} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** The conversation's upcoming meetings at the top of its "Calls & notes" panel (meetings on). */
export function ConversationMeetingsSection({ channelId }: { channelId: number }) {
  const { t } = useAppTranslation();
  const enabled = useCanScheduleMeetings();
  if (!enabled) return null;
  return (
    <section className="border-b p-3" aria-label={t('messaging.meetings.upcoming', 'Upcoming meetings')} data-testid="conversation-meetings">
      <h3 className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <CalendarClock className="h-3 w-3" aria-hidden="true" />
        {t('messaging.meetings.upcoming', 'Upcoming meetings')}
      </h3>
      <UpcomingMeetingsList channelId={channelId} />
    </section>
  );
}
